import { endOfMonth, startOfMonth } from 'date-fns';
import { baseApi } from 'shared/api/base-api';
import { matchesMapper } from '../domain/data-mappers/matches-mapper';
import { CalendarGame } from '../domain/interfaces/calendar';
import { MatchDto } from '../domain/interfaces/dtos';
import { fromMonthKey, toDayKey } from '../domain/utils/calendar-months';

const getMatchesByDateRangeQuery = `#graphql
  query getMatchesByDateRange($tournamentId: Int!, $dateFrom: String!, $dateTo: String!) {
    matchesByDate(input: { tournamentId: $tournamentId, limit: 500, dateFrom: $dateFrom, dateTo: $dateTo }) {
      id
      teams {
        id
        name
        emblem {
          externalUrl
        }
      }
      date
      sportType
      teamMatches {
        team {
          id
        }
        score
      }
      round {
        id
        name
        stage {
          id
        }
      }
      championship {
        id
        name
        tournament {
          id
          address {
            city
          }
        }
        settings {
          id
          name
          value
        }
      }
    }
  }
`;

export interface GetMonthMatchesArgs {
  tournamentId: number;
  /** Месяц `yyyy-MM` (локальный). */
  month: string;
}

/** Матчи месяца по дням: ключ `yyyy-MM-dd` (локальный день), значение — матчи этого дня. */
export type MonthMatches = Record<string, CalendarGame[]>;

interface MatchesByDateResponseDto {
  matchesByDate: MatchDto[];
}

const scheduleApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    /**
     * Матчи одного месяца. Месяц — отдельный запрос со своим потолком `limit` бэкенда, а не один на весь диапазон
     * показанных месяцев: у крупного организатора за несколько месяцев матчей может быть больше потолка, и хвост
     * молча пропал бы. Кеш — на каждую пару «организация + месяц»: смена организации чужих матчей не показывает.
     */
    getMonthMatches: build.query<MonthMatches, GetMonthMatchesArgs>({
      query: ({ tournamentId, month }) => {
        const startDate = startOfMonth(new Date(fromMonthKey(month).year, fromMonthKey(month).month));
        const endDate = endOfMonth(startDate);
        endDate.setHours(23, 59, 59, 999);

        return {
          query: getMatchesByDateRangeQuery,
          variables: { tournamentId, dateFrom: startDate.toISOString(), dateTo: endDate.toISOString() },
        };
      },
      transformResponse: (raw: MatchesByDateResponseDto): MonthMatches => {
        const matchesByDay: MonthMatches = {};

        for (const days of Object.values(matchesMapper(raw.matchesByDate))) {
          for (const day of days) {
            matchesByDay[toDayKey(new Date(day.date))] = day.matches;
          }
        }

        return matchesByDay;
      },
    }),
  }),
  overrideExisting: false,
});

export const { useGetMonthMatchesQuery } = scheduleApi;
